import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SiteHeader from "@/components/SiteHeader";

const DIVISIONS = [
  "Minimumweight (up to 105 lbs)", "Light Flyweight (up to 108 lbs)", "Flyweight (up to 112 lbs)",
  "Super Flyweight (up to 115 lbs)", "Bantamweight (up to 118 lbs)", "Super Bantamweight (up to 122 lbs)",
  "Featherweight (up to 126 lbs)", "Super Featherweight (up to 130 lbs)", "Lightweight (up to 135 lbs)",
  "Super Lightweight (up to 140 lbs)", "Welterweight (up to 147 lbs)", "Super Welterweight (up to 154 lbs)",
  "Middleweight (up to 160 lbs)", "Super Middleweight (up to 168 lbs)", "Light Heavyweight (up to 175 lbs)",
  "Cruiserweight (up to 200 lbs)", "Bridgerweight (up to 224 lbs)", "Heavyweight (over 224 lbs)",
];

const inputCls =
  "w-full bg-[#0d0d0d] border border-zinc-700 focus:border-[#E10600] outline-none px-4 py-3.5 text-white placeholder:text-zinc-600 transition-colors duration-300";
const labelCls = "block font-mono2 text-[10px] tracking-[0.3em] text-zinc-400 uppercase mb-2";

export default function RegisterPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "", age: "", experience_level: "professional",
    weight_division: "", pro_record: "", email: "", phone: "", password: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, age: Number(form.age) });
      setUser(data.user);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen" data-testid="register-page">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-8 pt-32 pb-24">
        <p className="font-mono2 text-[10px] tracking-[0.4em] text-[#E10600] uppercase mb-4 flex items-center gap-4">
          <span className="h-px w-10 bg-[#E10600]" /> Fighter Registration
        </p>
        <h1 className="font-display text-5xl sm:text-7xl uppercase leading-[0.9] text-white mb-6">
          Step back <span className="text-[#E10600]">through the ropes</span>
        </h1>
        <p className="text-base text-zinc-400 leading-relaxed mb-8 max-w-2xl">
          Masters Pro Boxing welcomes professionals and freshers aged 40–55. Fill in your fighter profile below —
          the MPB sanctioning committee reviews every application against the matchmaking parity standard, and once
          approved your bouts, results and official record appear in your Locker Room.
        </p>

        {!done ? (
          <motion.form
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={submit}
            className="border border-zinc-800 bg-[#111111] p-6 sm:p-10 space-y-6"
            data-testid="register-form"
          >
            <div>
              <label className={labelCls} htmlFor="reg-name">Full Name</label>
              <input id="reg-name" data-testid="register-fullname-input" required className={inputCls} placeholder="e.g. James Sullivan" value={form.full_name} onChange={set("full_name")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="reg-age">Age (40–55)</label>
              <input id="reg-age" data-testid="register-age-input" required type="number" min="40" max="55" className={inputCls} placeholder="e.g. 46" value={form.age} onChange={set("age")} />
            </div>
            <div>
              <span className={labelCls}>Experience Level</span>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { v: "professional", t: "Professional", s: "I have a pro record" },
                  { v: "fresher", t: "Fresher", s: "No pro bouts yet" },
                ].map((o) => (
                  <button
                    type="button"
                    key={o.v}
                    data-testid={`register-exp-${o.v}`}
                    onClick={() => setForm((f) => ({ ...f, experience_level: o.v }))}
                    className={`border p-5 text-left transition-colors duration-300 ${
                      form.experience_level === o.v
                        ? "border-[#E10600] bg-[#E10600]/10"
                        : "border-zinc-700 hover:border-zinc-500"
                    }`}
                  >
                    <span className="font-cond font-bold text-lg uppercase tracking-wide text-white block">{o.t}</span>
                    <span className="font-mono2 text-[9px] tracking-[0.2em] text-zinc-500 uppercase">{o.s}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls} htmlFor="reg-division">Weight Division</label>
              <select id="reg-division" data-testid="register-division-select" required className={inputCls} value={form.weight_division} onChange={set("weight_division")}>
                <option value="" disabled>Select division</option>
                {DIVISIONS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            {form.experience_level === "professional" && (
              <div>
                <label className={labelCls} htmlFor="reg-record">Pro Record (W-L-D)</label>
                <input id="reg-record" data-testid="register-record-input" className={inputCls} placeholder="Wins-Losses-Draws, e.g. 24-3-1" value={form.pro_record} onChange={set("pro_record")} />
              </div>
            )}
            <div>
              <label className={labelCls} htmlFor="reg-email">Email Address</label>
              <input id="reg-email" data-testid="register-email-input" required type="email" className={inputCls} placeholder="you@example.com" value={form.email} onChange={set("email")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="reg-phone">Phone Number</label>
              <input id="reg-phone" data-testid="register-phone-input" required type="tel" className={inputCls} placeholder="+91 98765 43210" value={form.phone} onChange={set("phone")} />
            </div>
            <div>
              <label className={labelCls} htmlFor="reg-password">Password (creates your fighter login)</label>
              <input id="reg-password" data-testid="register-password-input" required type="password" minLength="6" className={inputCls} placeholder="Min. 6 characters" value={form.password} onChange={set("password")} />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full font-cond font-semibold tracking-[0.25em] text-lg bg-[#E10600] hover:bg-[#C00500] disabled:opacity-50 text-white py-4 transition-colors duration-300"
            >
              {loading ? "SUBMITTING..." : "SUBMIT REGISTRATION"}
            </button>
            <p className="text-center text-xs text-zinc-500">
              Already registered?{" "}
              <Link to="/login" data-testid="register-login-link" className="text-[#E10600] hover:underline">Fighter login</Link>
            </p>
          </motion.form>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="border border-green-800/60 bg-[#101410] p-8 sm:p-10 text-center"
            data-testid="registration-success"
          >
            <p className="font-mono2 text-[10px] tracking-[0.35em] text-green-500 uppercase mb-3">Application received</p>
            <h2 className="font-display text-4xl sm:text-5xl uppercase text-white mb-4">You're in the books</h2>
            <p className="text-sm text-zinc-400 leading-relaxed mb-8 max-w-md mx-auto">
              The MPB sanctioning committee is reviewing your application. Once approved, your scheduled
              bouts, results and official record will appear in your Locker Room.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              data-testid="goto-dashboard-button"
              className="font-cond font-semibold tracking-[0.25em] bg-[#E10600] hover:bg-[#C00500] text-white px-10 py-4 transition-colors duration-300"
            >
              GO TO MY LOCKER ROOM
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
